import React, { useContext, useEffect, useState } from 'react'
import { Trans, t } from '@lingui/macro'

import { Drawer } from '../../../components/common/page/drawer/Drawer'
import DrawerItem from '../../../components/common/page/drawer/DrawerItem'
import Input from '../../../components/common/form/input/Input'
import { Modal } from '../../../components/common/modal/Modal'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import { notification } from '../../../components/common/notifications/Notifications'
import {
  ButtonClean,
  ButtonDanger,
  ButtonPrimary,
  Switch
} from 'front-end/src/components'

import Select, { SelectVariant } from '../../../components/common/select/Select'
import { ManageCampaignContext } from '../ManageCampaignPage'

import './ManageCampaignDrawer.less'

import { apolloClientPrivate } from '../../../graphql/apolloClient'
import { showCampaignActivationUpcomingActionsError } from '../campaign-overview/CampaignReadinessHero'
import { isCampaignFixed, isCampaignImmediate } from '../utils'
import { AccessibilityLabel } from '../../../components/common/AccessibilityLabel'

import { CampaignScheduleEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { useActivateCampaignMutation } from '../campaign-overview/CampaignReadinessHero.codegen'
import {
  FixedActionsComingUpInActivationWindowQuery,
  FixedActionsComingUpInActivationWindowQueryVariables,
  FixedActionsComingUpInActivationWindowDocument
} from '../ManageCampaignPage.codegen'
import {
  useCampaignPatchMutation,
  useArchiveCampaignMutation,
  useSetCampaignTrackLinksMutation
} from './ManageCampaignDrawer.codegen'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'

export interface IManageCampaignDrawerProps {
  show?: boolean
  onHide: () => void
  onUpdate: () => void
}

export const ManageCampaignDrawer: React.FC<IManageCampaignDrawerProps> = (
  props
) => {
  const [isActive, setIsActive] = useState<boolean>(false)

  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false)
  const [deactivateModalVisible, setDeactivateModalVisible] =
    useState<boolean>(false)
  const [
    deactivateLinksTrackingModalVisible,
    setDeactivateLinksTrackingModalVisible
  ] = useState<boolean>(false)
  const [checkingUpcomingFixedActions, setCheckingUpcomingFixedActions] =
    useState(false)

  const { featureFlags } = useFeatureFlags()
  const [patchCampaign] = useCampaignPatchMutation()
  const [archiveCampaign, { loading: isArchiving }] =
    useArchiveCampaignMutation()
  const [setTrackLinks] = useSetCampaignTrackLinksMutation()
  const { campaign, refetchCampaign } = useContext(ManageCampaignContext)

  async function updateCampaign(input) {
    try {
      await patchCampaign({
        variables: {
          id: campaign.id,
          input
        }
      })
      refetchCampaign()
    } catch (e) {
      notification.error(t`Error campaign update failed`)
    }
  }

  const [activateCampaign, { loading }] = useActivateCampaignMutation()

  useEffect(() => {
    setIsActive(campaign.is_active)
  }, [])

  const options = [
    {
      label: t`Fixed (date)`,
      value: CampaignScheduleEnum.FIXED
    },
    {
      label: t`Relative (days)`,
      value: CampaignScheduleEnum.RELATIVE
    },
    {
      label: t`Immediate`,
      value: CampaignScheduleEnum.IMMEDIATE
    }
  ]

  const getSelectedOption: number = options.findIndex((option) => {
    return option.value === campaign.schedule_type
  })

  const setCampaignStatus = async (targetStatus: boolean) => {
    let campaignData

    if (targetStatus) {
      if (isCampaignFixed(campaign)) {
        setCheckingUpcomingFixedActions(true)

        // lazy query or a query with skip couldn't be used here unfortunatelly, apollo client sucks: https://github.com/apollographql/react-apollo/issues/3499
        const res = await apolloClientPrivate.mutate<
          FixedActionsComingUpInActivationWindowQuery,
          FixedActionsComingUpInActivationWindowQueryVariables
        >({
          mutation: FixedActionsComingUpInActivationWindowDocument,
          variables: { id: campaign.id }
        })
        campaignData = res?.data?.admin?.campaign

        setCheckingUpcomingFixedActions(false)

        if (
          campaignData.fixedActionsComingUpInActivationWindow &&
          campaignData.fixedActionsComingUpInActivationWindow.length > 0
        ) {
          showCampaignActivationUpcomingActionsError(campaignData)
          return
        }
      }
    }

    setIsActive(targetStatus)
    try {
      await activateCampaign({
        variables: { id: campaign.id, active: targetStatus }
      })
      await refetchCampaign()
    } catch (err) {
      console.error(err)
      setIsActive(!targetStatus)
    }
  }

  return (
    <>
      <Drawer
        className="ManageCampaignDrawer"
        title={t`Settings`}
        show={props.show}
        onHide={props.onHide}
      >
        <DrawerItem label={t`Name`}>
          <Input
            title={t`Name`}
            light
            defaultValue={campaign.name}
            disabled={campaign.wasCampaignDeactivated}
            debouncedOnChange={async (value) => {
              await updateCampaign({
                name: value
              })
            }}
          />
        </DrawerItem>
        <DrawerItem label={t`Activate campaign`}>
          {isCampaignImmediate(campaign) && (
            <ButtonPrimary
              disabled={loading || isActive}
              onClick={async () => {
                if (!isActive) {
                  await setCampaignStatus(!isActive)
                }
              }}
            >
              {isActive ? <Trans>Active</Trans> : <Trans>Activate</Trans>}
            </ButtonPrimary>
          )}
          {!isCampaignImmediate(campaign) && (
            <Switch
              isDisabled={
                loading ||
                checkingUpcomingFixedActions ||
                campaign.wasCampaignDeactivated
              }
              onChange={async () => {
                if (isActive) {
                  // Going to deactivate, first show Modal window to user
                  setDeactivateModalVisible(true)
                } else {
                  await setCampaignStatus(!isActive)
                }
              }}
              isChecked={isActive}
            />
          )}
        </DrawerItem>
        <DrawerItem
          label={t`Track links`}
          hint={t`All links in actions will be tracked. Data will be shown in the Link reports tab.`}
        >
          <Switch
            isDisabled={
              campaign.wasCampaignDeactivated ||
              (campaign.is_active && isCampaignImmediate(campaign))
            }
            onChange={async () => {
              if (campaign.track_links && campaign.is_active) {
                setDeactivateLinksTrackingModalVisible(true)
              } else {
                await setTrackLinks({
                  variables: {
                    id: campaign.id,
                    trackLinks: !campaign.track_links
                  }
                })
              }
            }}
            isChecked={campaign.track_links}
          />
        </DrawerItem>
        {featureFlags.campaigns_archive_cert_enrolments_after_user_leave && (
          <DrawerItem
            label={t`Auto archive certification enrolments`}
            hint={t`With this feature toggled on, when a user no longer meets the audience filter(s) and is therefore removed from the campaign, their certification enrolment will be automatically archived.`}
          >
            <Switch
              onChange={async () => {
                await updateCampaign({
                  auto_archive_cert_enrols: !campaign.auto_archive_cert_enrols
                })
              }}
              isChecked={campaign.auto_archive_cert_enrols}
            />
          </DrawerItem>
        )}
        <DrawerItem className="danger" label={t`Delete this campaign`}>
          <ButtonDanger
            onClick={() => {
              setDeleteModalVisible(true)
            }}
          >
            <Trans>Delete</Trans>
          </ButtonDanger>
        </DrawerItem>
        <DrawerItem
          label={t`Schedule type`}
          hint={
            campaign.is_active
              ? t`Changing schedule type is not possible when campaign is active.`
              : ''
          }
        >
          <AccessibilityLabel text={t`Schedule`}>
            <Select
              options={options}
              variant={SelectVariant.WHITE}
              defaultValue={options[getSelectedOption]}
              isDisabled={campaign.is_active || campaign.wasCampaignDeactivated}
              // @ts-expect-error 9/11/2020
              onChange={async (option: { label: string; value: string }) => {
                await updateCampaign({
                  schedule_type: option.value as CampaignScheduleEnum
                })
              }}
              className="scheduleSelect"
            />
          </AccessibilityLabel>
        </DrawerItem>
      </Drawer>

      <Modal
        header={t`Delete campaign`}
        show={deleteModalVisible}
        controls={[
          <ButtonClean
            onClick={() => {
              setDeleteModalVisible(false)
            }}
            key={1}
          >
            <Trans>Cancel</Trans>
          </ButtonClean>,
          <ButtonDanger
            key={2}
            isLoading={isArchiving}
            onClick={async () => {
              try {
                await archiveCampaign({
                  variables: {
                    id: campaign.id
                  }
                })

                notification.info(t`Campaign has been deleted`)
                navigateTo(linkBuilder.campaigns())
              } catch (err) {
                notification.error(t`Error deleting campaign: ${err.message}`)
                console.error(err)
              }
            }}
          >
            <Trans>Delete this campaign</Trans>
          </ButtonDanger>
        ]}
        onHide={() => {
          setDeleteModalVisible(false)
        }}
      >
        <p>
          <Trans>
            Are you sure you want to delete this campaign?
            <br />
            This action cannot be undone
          </Trans>
        </p>
      </Modal>

      <Modal
        header={t`Deactivate campaign`}
        show={deactivateModalVisible}
        controls={[
          <ButtonClean
            onClick={() => {
              setDeactivateModalVisible(false)
            }}
            key={1}
          >
            <Trans>Cancel</Trans>
          </ButtonClean>,
          <ButtonDanger
            key={2}
            onClick={async () => {
              await setCampaignStatus(false)
              setDeactivateModalVisible(false)
            }}
          >
            <Trans>Deactivate this campaign</Trans>
          </ButtonDanger>
        ]}
        onHide={() => {
          setDeleteModalVisible(false)
        }}
      >
        <p>
          <Trans>
            Are you sure you want to deactivate this campaign?
            <br />
            Deactivated campaign cannot be activated again. In order to launch
            campaign with this setup, please copy this campaign in campaigns
            overview.
          </Trans>
        </p>
      </Modal>

      <Modal
        header={t`Stop tracking links`}
        show={deactivateLinksTrackingModalVisible}
        controls={[
          <ButtonClean
            onClick={() => {
              setDeactivateLinksTrackingModalVisible(false)
            }}
            key={1}
          >
            <Trans>Cancel</Trans>
          </ButtonClean>,
          <ButtonDanger
            key={2}
            onClick={async () => {
              await setTrackLinks({
                variables: {
                  id: campaign.id,
                  trackLinks: !campaign.track_links
                }
              })

              setDeactivateLinksTrackingModalVisible(false)
            }}
          >
            <Trans>Stop tracking</Trans>
          </ButtonDanger>
        ]}
        onHide={() => {
          setDeleteModalVisible(false)
        }}
      >
        <p>
          <Trans>
            Are you sure you want to stop tracking links?
            <br />
            This action cannot be undone
          </Trans>
        </p>
      </Modal>
    </>
  )
}
